import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FaPlus,
  FaRobot,
  FaEdit,
  FaInstagram,
  FaExternalLinkAlt,
  FaChartPie,
  FaTimes,
  FaSpinner,
  FaRegSmile,
  FaRegFrown,
  FaRegMeh,
  FaLightbulb,
  FaUserCircle,
} from "react-icons/fa";

import companyAutomationService from "../../services/companyAutomationService";

const COMPANY_DM_BASE = "/company/dashboard/dm-automation";

/**
 * Truncate-with-toggle helper used in dense table cells. Pulled out
 * of the influencer dashboard pattern so the brand UI stays
 * consistent with what the team has shipped before.
 */
const TruncatedText = ({ text, maxLength = 50 }) => {
  const [expanded, setExpanded] = useState(false);
  const value = text || "";

  if (!value.trim()) {
    return <span className="italic text-gray-400">No content</span>;
  }

  const shouldTruncate = value.length > maxLength;
  const display = expanded || !shouldTruncate ? value : value.slice(0, maxLength);

  return (
    <span className="text-gray-700">
      {display}
      {shouldTruncate && !expanded && "..."}
      {shouldTruncate && (
        <button
          type="button"
          onClick={() => setExpanded((p) => !p)}
          className="ml-1 text-[8px] md:text-xs font-medium text-orange-600 hover:text-orange-700"
        >
          {expanded ? "Show less" : "Show more"}
        </button>
      )}
    </span>
  );
};

const AUTOMATION_TYPE_LABEL = {
  DM: "Direct Message",
  Comments: "Comments",
  Both: "DM & Comments",
};

/**
 * DmAutomationPage (Brand DM Automation Dashboard)
 * ------------------------------------------------------------------
 * The landing page for brand-side Instagram DM automation. It:
 *   1. Shows a "connect Instagram" CTA if no IG account is linked.
 *   2. Lists all existing automations with status and quick stats.
 *   3. Exposes "Create New" and per-row edit / pause toggles.
 *
 * Network model: the page makes two parallel calls on mount –
 *   - getStatus()  → instagram connection + counts
 *   - listAutomations()
 * Both are cheap server-side, but we render the connection check
 * first so users with no IG never see an empty automations table.
 */
const DmAutomationPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [status, setStatus] = useState(null);
  const [automations, setAutomations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [togglingId, setTogglingId] = useState(null);

  // AI comment analysis modal state. Analysis is ONLY triggered by an
  // explicit user click (never on mount) to keep Gemini costs down.
  const [analyzingId, setAnalyzingId] = useState(null);
  const [analysisModal, setAnalysisModal] = useState(null); // { automation, result }

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const [statusRes, listRes] = await Promise.all([
        companyAutomationService.getStatus(),
        companyAutomationService.listAutomations(),
      ]);
      setStatus(statusRes?.data || null);
      setAutomations(Array.isArray(listRes?.data) ? listRes.data : []);
    } catch (err) {
      console.error("Failed to load DM automation dashboard", err);
      toast.error(err.message || "Failed to load DM Automation");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll, location.pathname]);

  const handleToggle = async (automation) => {
    try {
      setTogglingId(automation._id);
      const response = await companyAutomationService.toggleAutomation(
        automation._id
      );
      if (response?.success) {
        toast.success(
          response?.data?.isActive
            ? "Automation activated"
            : "Automation paused"
        );
        await fetchAll();
      } else {
        toast.error(response?.message || "Failed to update automation");
      }
    } catch (err) {
      toast.error(err.message || "Failed to update automation");
    } finally {
      setTogglingId(null);
    }
  };

  const handleAnalyze = async (automation) => {
    try {
      setAnalyzingId(automation._id);
      const result = await companyAutomationService.analyzeComments(
        automation._id
      );
      if (result?.success) {
        setAnalysisModal({ automation, result });
        toast.success(
          result.cached ? "Loaded cached analysis" : "Fresh analysis ready"
        );
      } else {
        toast.error(result?.message || "Failed to analyze comments");
      }
    } catch (err) {
      // Friendly handling for the most common "no data" / AI errors.
      if (err.code === "NOT_ENOUGH_COMMENTS") {
        toast.info(err.message || "Not enough comments to analyze yet");
      } else if (err.code === "GEMINI_INVALID_RESPONSE") {
        toast.error("AI is busy right now. Please try again shortly.");
      } else {
        toast.error(err.message || "Failed to analyze comments");
      }
    } finally {
      setAnalyzingId(null);
    }
  };

  const instagramConnected = Boolean(status?.instagram?.connected);

  return (
    <div className="bg-[#F4F2EF] h-full w-full">
      <div className="w-full rounded-lg border border-orange-50 bg-white/70 p-2 shadow-sm md:p-4">
        {/* Header */}
        <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <h1 className="text-sm md:text-xl font-bold tracking-tight text-gray-900">
              DM Automation
            </h1>
            <p className="mt-1 max-w-2xl text-[10px] md:text-xs text-gray-600">
              Trigger personalised Instagram DMs the moment a follower comments
              the right keyword on your brand&apos;s posts.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate(`${COMPANY_DM_BASE}/create`)}
              disabled={!instagramConnected}
              className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold text-white transition md:px-4 md:text-sm ${
                instagramConnected
                  ? "bg-orange-600 hover:bg-orange-700"
                  : "cursor-not-allowed bg-orange-300"
              }`}
              title={
                instagramConnected
                  ? "Create a new automation"
                  : "Connect Instagram first"
              }
            >
              <FaPlus className="h-3 w-3" />
              New Automation
            </button>
          </div>
        </header>

        <div className="mt-4 -mx-4 h-px bg-[#E3DFD9] sm:-mx-4 lg:-mx-6" />

        {/* Status bar — on mobile: Instagram full width, then Total + Active side by side */}
        <div className="mt-6 grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3">
          <StatCard
            className="col-span-2 sm:col-span-1"
            label="Instagram"
            value={
              instagramConnected
                ? `@${status?.instagram?.username || "connected"}`
                : "Not connected"
            }
            tone={instagramConnected ? "success" : "warning"}
            cta={
              instagramConnected
                ? null
                : {
                    label: "Connect",
                    onClick: () =>
                      navigate("/company/dashboard/social-accounts"),
                  }
            }
            Icon={FaInstagram}
          />
          <StatCard
            label="Total Automations"
            value={loading ? "…" : String(status?.automationCount ?? 0)}
            Icon={FaRobot}
          />
          <StatCard
            label="Active"
            value={loading ? "…" : String(status?.activeCount ?? 0)}
            tone="success"
            Icon={FaRobot}
          />
        </div>

        {/* Connection CTA when no IG */}
        {!loading && !instagramConnected && (
          <div className="mt-6 flex flex-col items-center justify-center rounded-2xl border border-dashed border-orange-200 bg-orange-50/40 px-6 py-10 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-orange-100 text-orange-600">
              <FaInstagram className="h-5 w-5" />
            </div>
            <h3 className="mt-4 text-sm md:text-base font-semibold text-gray-900">
              Connect your brand&apos;s Instagram first
            </h3>
            <p className="mt-1 max-w-md text-[11px] md:text-sm text-gray-600">
              You need a connected Instagram Business account to send DMs.
              Multi-account support is coming soon.
            </p>
            <button
              type="button"
              onClick={() => navigate("/company/dashboard/social-accounts")}
              className="mt-5 rounded-lg bg-orange-600 px-4 py-2 text-xs md:text-sm font-semibold text-white transition hover:bg-orange-700"
            >
              Connect Instagram
            </button>
          </div>
        )}

        {/* Table / empty state */}
        {instagramConnected && (
          <div className="mt-6">
            {loading ? (
              <div className="rounded-xl border border-gray-200 bg-white p-6 text-xs text-gray-500">
                Loading automations…
              </div>
            ) : automations.length === 0 ? (
              <EmptyState
                onCreate={() => navigate(`${COMPANY_DM_BASE}/create`)}
              />
            ) : (
              <AutomationTable
                automations={automations}
                togglingId={togglingId}
                analyzingId={analyzingId}
                onEdit={(automation) =>
                  navigate(`${COMPANY_DM_BASE}/edit/${automation._id}`)
                }
                onToggle={handleToggle}
                onAnalyze={handleAnalyze}
              />
            )}
          </div>
        )}
      </div>

      {analysisModal && (
        <AnalysisModal
          automation={analysisModal.automation}
          result={analysisModal.result}
          onClose={() => setAnalysisModal(null)}
        />
      )}
    </div>
  );
};

/* ----------------------------- Sub-components ----------------------------- */

const StatCard = ({
  label,
  value,
  tone = "default",
  cta,
  Icon,
  className = "",
}) => {
  const toneStyles = {
    success: "border-emerald-100 bg-emerald-50",
    warning: "border-amber-100 bg-amber-50",
    default: "border-gray-100 bg-white",
  };
  return (
    <div
      className={`flex min-w-0 flex-col rounded-xl border p-2 sm:p-3 ${toneStyles[tone]} ${className}`}
    >
      <div className="flex min-w-0 items-center justify-between gap-1">
        <div className="flex min-w-0 items-center gap-1.5 sm:gap-2">
          {Icon ? (
            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white text-gray-500 ring-1 ring-gray-100 sm:h-8 sm:w-8">
              <Icon className="h-2.5 w-2.5 sm:h-3.5 sm:w-3.5" />
            </span>
          ) : null}
          <p className="truncate text-[8px] font-semibold uppercase tracking-wide text-gray-500 sm:text-[10px]">
            {label}
          </p>
        </div>
        {cta ? (
          <button
            type="button"
            onClick={cta.onClick}
            className="shrink-0 rounded-md bg-white px-1.5 py-0.5 text-[8px] font-semibold text-orange-700 ring-1 ring-orange-200 transition hover:bg-orange-50 sm:px-2 sm:py-1 sm:text-[10px]"
          >
            {cta.label}
          </button>
        ) : null}
      </div>
      <p className="mt-1 w-full truncate text-left text-[8px] md:text-xs ml-6 font-semibold text-gray-900 sm:text-sm">
        {value}
      </p>
    </div>
  );
};

const EmptyState = ({ onCreate }) => (
  <div className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-gray-200 bg-gray-50 px-6 py-12 text-center">
    <FaRobot className="h-10 w-10 text-gray-400" />
    <h3 className="mt-4 text-sm md:text-base font-semibold text-gray-800">
      No automations yet
    </h3>
    <p className="mt-1 max-w-md text-[11px] md:text-sm text-gray-500">
      Create your first automation to auto-DM commenters with product links,
      offers, and onboarding messages.
    </p>
    <button
      type="button"
      onClick={onCreate}
      className="mt-5 inline-flex items-center gap-2 rounded-lg bg-orange-600 px-4 py-2 text-xs md:text-sm font-semibold text-white transition hover:bg-orange-700"
    >
      <FaPlus className="h-3 w-3" />
      Create Automation
    </button>
  </div>
);

const AutomationTable = ({
  automations,
  togglingId,
  analyzingId,
  onEdit,
  onToggle,
  onAnalyze,
}) => (
  <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            {[
              "S.N",
              "Post",
              "Type",
              "Keywords",
              "Message",
              "Status",
              "Actions",
            ].map((h) => (
              <th
                key={h}
                className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-500"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 bg-white text-xs">
          {automations.map((automation, idx) => (
            <tr key={automation._id} className="hover:bg-gray-50">
              <td className="px-3 py-3 font-medium text-gray-800">{idx + 1}</td>
              <td className="px-3 py-3">
                <div className="flex flex-col">
                  <span className="font-medium text-gray-900">
                    {automation.uploadPost?.title || "Untitled"}
                  </span>
                  {automation.uploadPost?.mediaLink && (
                    <a
                      href={automation.uploadPost.mediaLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-0.5 inline-flex items-center gap-1 text-[8px] md:text-xs text-orange-600 hover:text-orange-700"
                    >
                      View post <FaExternalLinkAlt className="h-2.5 w-2.5" />
                    </a>
                  )}
                </div>
              </td>
              <td className="px-3 py-3">
                <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-[8px] md:text-xs font-medium text-blue-700">
                  {AUTOMATION_TYPE_LABEL[automation.typeOfAutomation?.type] ||
                    automation.typeOfAutomation?.type}
                </span>
              </td>
              <td className="px-3 py-3">
                <div className="flex flex-wrap gap-1">
                  {automation.keywords?.length ? (
                    automation.keywords.slice(0, 4).map((kw) => (
                      <span
                        key={kw}
                        className="rounded-md bg-gray-100 px-1.5 py-0.5 text-[8px] md:text-xs font-medium text-gray-700"
                      >
                        {kw}
                      </span>
                    ))
                  ) : (
                    <span className="italic text-gray-400">—</span>
                  )}
                  {automation.keywords?.length > 4 && (
                    <span className="rounded-md bg-gray-50 px-1.5 py-0.5 text-[8px] md:text-xs text-gray-500">
                      +{automation.keywords.length - 4}
                    </span>
                  )}
                </div>
              </td>
              <td className="px-3 py-3 max-w-xs">
                <TruncatedText text={automation.message} maxLength={45} />
              </td>
              <td className="px-2 py-3 md:px-3">
                <div className="flex flex-col items-center gap-1 md:flex-row md:items-center md:gap-2">
                  <button
                    type="button"
                    onClick={() => onToggle(automation)}
                    disabled={togglingId === automation._id}
                    className={`relative inline-flex h-4 w-8 shrink-0 items-center rounded-full transition-colors md:h-5 md:w-10 ${
                      automation.isActive
                        ? "bg-emerald-500 hover:bg-emerald-600"
                        : "bg-gray-300 hover:bg-gray-400"
                    } ${
                      togglingId === automation._id
                        ? "cursor-wait opacity-70"
                        : "cursor-pointer"
                    }`}
                  >
                    <span
                      className={`inline-block h-3 w-3 transform rounded-full bg-white shadow transition-transform md:h-4 md:w-4 ${
                        automation.isActive
                          ? "translate-x-4 md:translate-x-5"
                          : "translate-x-0.5 md:translate-x-1"
                      }`}
                    />
                  </button>
                  <span
                    className={`text-[8px] md:text-xs font-semibold leading-none md:text-[10px] ${
                      automation.isActive
                        ? "text-emerald-600"
                        : "text-gray-400"
                    }`}
                  >
                    {automation.isActive ? "Active" : "Paused"}
                  </span>
                </div>
              </td>
              <td className="px-3 py-3">
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => onAnalyze(automation)}
                    disabled={analyzingId === automation._id}
                    className={`inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-[8px] md:text-xs font-semibold transition ${
                      analyzingId === automation._id
                        ? "cursor-wait bg-orange-50 text-orange-400"
                        : "text-orange-600 hover:bg-orange-50"
                    }`}
                    title="Analyze comments with AI"
                  >
                    {analyzingId === automation._id ? (
                      <FaSpinner className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <FaChartPie className="h-3.5 w-3.5" />
                    )}
                    <span className="hidden md:inline">
                      {analyzingId === automation._id
                        ? "Analyzing…"
                        : "Analyze"}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => onEdit(automation)}
                    className="inline-flex items-center gap-1 rounded-md p-1.5 text-[8px] md:text-xs text-blue-600 transition hover:bg-blue-50"
                    title="Edit automation"
                  >
                    <FaEdit className="h-3.5 w-3.5" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

/**
 * AnalysisModal
 * ------------------------------------------------------------------
 * Read-only presentation of an AI comment analysis result. It does NOT
 * trigger any network call itself – the parent fetches the analysis on
 * an explicit click and passes the result in.
 */
const AnalysisModal = ({ automation, result, onClose }) => {
  const analysis = result?.analysis || {};
  const meta = result?.meta || {};
  const cached = Boolean(result?.cached);

  const positive = analysis.positiveCount || 0;
  const negative = analysis.negativeCount || 0;
  const neutral = analysis.neutralCount || 0;
  const total = positive + negative + neutral || 1;

  const pct = (n) => Math.round((n / total) * 100);

  const analyzedAt = meta.analyzedAt
    ? new Date(meta.analyzedAt).toLocaleString()
    : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 md:p-4 p-2"
      onClick={onClose}
    >
      <div
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white p-3 md:p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <FaChartPie className="h-4 w-4 text-orange-600" />
              <h2 className="text-sm font-bold text-gray-900 md:text-base">
                Comment Analysis
              </h2>
              <span
                className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                  cached
                    ? "bg-gray-100 text-gray-600"
                    : "bg-emerald-50 text-emerald-700"
                }`}
              >
                {cached ? "Cached" : "Fresh analysis"}
              </span>
            </div>
            <p className="mt-1 truncate text-[11px] text-gray-500">
              {automation?.uploadPost?.title || "Untitled post"}
              {meta.analyzedCommentCount != null && (
                <> · {meta.analyzedCommentCount} comments analyzed</>
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1.5 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
            title="Close"
          >
            <FaTimes className="h-4 w-4" />
          </button>
        </div>

        {/* Sentiment cards */}
        <div className="mt-4 grid grid-cols-3 gap-2">
          <SentimentCard
            label="Positive"
            value={positive}
            pct={pct(positive)}
            tone="positive"
            Icon={FaRegSmile}
          />
          <SentimentCard
            label="Neutral"
            value={neutral}
            pct={pct(neutral)}
            tone="neutral"
            Icon={FaRegMeh}
          />
          <SentimentCard
            label="Negative"
            value={negative}
            pct={pct(negative)}
            tone="negative"
            Icon={FaRegFrown}
          />
        </div>

        {/* Sentiment bar */}
        <div className="mt-3 flex h-2 w-full overflow-hidden rounded-full bg-gray-100">
          <div
            className="bg-emerald-500"
            style={{ width: `${pct(positive)}%` }}
          />
          <div className="bg-gray-300" style={{ width: `${pct(neutral)}%` }} />
          <div className="bg-rose-500" style={{ width: `${pct(negative)}%` }} />
        </div>

        {/* Persona */}
        {analysis.persona && (
          <div className="mt-4 rounded-xl border border-orange-100 bg-orange-50/50 p-3">
            <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-orange-700">
              <FaUserCircle className="h-3.5 w-3.5" />
              Audience Persona
            </div>
            <p className="mt-1 text-xs text-gray-700 md:text-sm">
              {analysis.persona}
            </p>
          </div>
        )}

        {/* Insights */}
        {Array.isArray(analysis.insights) && analysis.insights.length > 0 && (
          <div className="mt-4">
            <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              <FaLightbulb className="h-3.5 w-3.5 text-amber-500" />
              Top Insights
            </div>
            <ul className="mt-2 space-y-2">
              {analysis.insights.map((insight, idx) => (
                <li
                  key={idx}
                  className="flex gap-2 rounded-lg border border-gray-100 bg-gray-50 p-2.5 text-xs text-gray-700"
                >
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-orange-100 text-[10px] font-bold text-orange-700">
                    {idx + 1}
                  </span>
                  <span>{insight}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {analyzedAt && (
          <p className="mt-4 text-right text-[10px] text-gray-400">
            Last analyzed: {analyzedAt}
          </p>
        )}
      </div>
    </div>
  );
};

const SentimentCard = ({ label, value, pct, tone, Icon }) => {
  const toneStyles = {
    positive: "border-emerald-100 bg-emerald-50 text-emerald-700",
    neutral: "border-gray-100 bg-gray-50 text-gray-600",
    negative: "border-rose-100 bg-rose-50 text-rose-700",
  };
  return (
    <div className={`rounded-xl border p-2.5 text-center ${toneStyles[tone]}`}>
      <Icon className="mx-auto h-4 w-4" />
      <p className="mt-1 text-lg font-bold leading-none">{value}</p>
      <p className="mt-1 text-[10px] font-medium uppercase tracking-wide">
        {label}
      </p>
      <p className="text-[10px] opacity-70">{pct}%</p>
    </div>
  );
};

export default DmAutomationPage;
